import { createReducer } from "@reduxjs/toolkit";
import { GetDelegatedPedestrianRightsQuery, PedestrianDelegeeDetails } from "../../api/pedestrianRights.api";
import { DelegatedPedestrianRight, PedestrianRightsMyFleetState } from "../../models/delegations/DelegatedPedestrianRight";
import { ErrorDetailsModel } from "../../models/ErrorDetailsModel";
import { LocatedParkingProduct } from "../../models/LocatedParkingProduct";
import { LocatedParkingProductModel, ParkingProductModel, ParkingProductType } from "../../models/ParkingProductModel";
import { PedestrianProductModel } from "../../models/PedestrianProductModel";
import {
    clearDelegeeDetailsAction,
    deletePedestrianDelegeesSuccessAction,
    disposeErrorAction,
    getPedestrianDelegeeDetailsActionThunk,
    getPedestrianDelegeesMyFleetFailureAction,
    getPedestrianDelegeesMyFleetRequestAction,
    getPedestrianDelegeesMyFleetSuccessAction,
    getPedestrianProductsThunk,
    inviteActionThunk,
    myProductsPedestrianRightsFailureAction,
    myProductsPedestrianRightsRequestAction,
    myProductsPedestrianRightsSuccessAction,
    resendInviteActionThunk,
    revokePedestrianRightsSuccessAction,
    toggleShowDeletePedestrianDelegeesAction,
    toggleShowRevokePedestrianAction,
    updatePedestrianDelegeeActionThunk,
} from "../actions/pedestrianRights.actions";
import * as pedestrianOptimisticUpdates from "../helpers/pedestrian-optimistic-updates";
import { DataType } from "./parking-products";
import { isLocatedParkingProductsSuccess } from "../actions/parkingProducts.matchers";

export type PedestrianRightsPagedData<T> = {
    totalPages: number;
    totalRecords: number;
    pages: {
        [page: number]: {
            loading: boolean;
            data?: T[] | null;
            error?: ErrorDetailsModel | null;
            query: GetDelegatedPedestrianRightsQuery;
        };
    };
}

export type PedestrianRightsState = {
    loading: boolean;
    myProducts: PedestrianRightsPagedData<DelegatedPedestrianRight>;
    myFleet: PedestrianRightsMyFleetState;
    pedestrianProducts: DataType<PedestrianProductModel[]>;
    error?: ErrorDetailsModel;
    delegeeDetails: DataType<PedestrianDelegeeDetails>;
    updateState: {
        loading: boolean;
        error?: ErrorDetailsModel;
    },
    showRevokePedestriansWarning: boolean;
    showDeletePedestrianDelegeesWarning: boolean;
    locatedParkingProducts: LocatedParkingProductModel[];
}

const initialState: PedestrianRightsState = {
    loading: false,
    error: undefined,
    myProducts: {
        pages: {},
        totalPages: 0,
        totalRecords: null!,
    },
    pedestrianProducts: {
        loading: false,
        data: [],
        error: null,
    },
    delegeeDetails: {
        loading: false,
        data: null,
        error: null,
    },
    updateState: {
        loading: false,
    },
    showRevokePedestriansWarning: false,
    showDeletePedestrianDelegeesWarning: false,
    myFleet: {
        delegees: {
            totalPages: 0,
            totalRecords: 0,
            pages: [
                {
                    loading: false,
                    data: [],
                    error: null,
                }
            ],
        },
        pageNumber: 0,
    },
    locatedParkingProducts: [],
}

export const pedestrianRightsReducer = createReducer(initialState, (builder) => {
    builder
        .addCase(myProductsPedestrianRightsRequestAction, (state, action) => {
            state.myProducts.pages[action.payload.query.page] = {
                loading: true,
                query: action.payload.query,
            };
        })
        .addCase(myProductsPedestrianRightsSuccessAction, (state, action) => {
            state.myProducts.totalPages = action.payload.totalPages;
            state.myProducts.totalRecords = action.payload.totalRecords;
            state.myProducts.pages[action.payload.query.page] = {
                loading: false,
                data: updateLocationName(action.payload.data, state.locatedParkingProducts),
                query: action.payload.query
            };
        })
        .addCase(myProductsPedestrianRightsFailureAction, (state, action) => {
            state.myProducts.pages[action.payload.query.page] = {
                loading: false,
                query: action.payload.query
            };
        })
        .addCase(getPedestrianProductsThunk.rejected, (state, action) => {
            state.pedestrianProducts.error = action.payload;
            state.pedestrianProducts.data = null;
            state.pedestrianProducts.loading = false;
        })
        .addCase(getPedestrianProductsThunk.fulfilled, (state, action) => {
            state.pedestrianProducts.data = action.payload;
            state.pedestrianProducts.loading = false;
        })
        .addCase(getPedestrianProductsThunk.pending, (state, _) => {
            state.pedestrianProducts.loading = true;
        })
        .addCase(inviteActionThunk.pending, (state, _) => {
            state.loading = true;
        })
        .addCase(inviteActionThunk.fulfilled, (state, action) => {
            state.myProducts = pedestrianOptimisticUpdates.reduceInvitePedestrianMyProducts(state.myProducts, state.pedestrianProducts.data!, action.meta.arg, action.payload)
            pedestrianOptimisticUpdates.applyOptimisticInviteOnMyFleet(state, action.meta.arg, action.payload);
            state.loading = false;
        })
        .addCase(inviteActionThunk.rejected, (state, action) => {
            state.error = action.payload;
            state.loading = false;
        })
        .addCase(clearDelegeeDetailsAction, (state, _) => {
            state.delegeeDetails = initialState.delegeeDetails;
        })
        .addCase(disposeErrorAction, (state, _) => {
            state.loading = initialState.loading;
            state.error = initialState.error;
            state.updateState.error = initialState.updateState.error;
        })
        .addCase(toggleShowRevokePedestrianAction, (state) => {
            state.showRevokePedestriansWarning = !state.showRevokePedestriansWarning;
        })
        .addCase(revokePedestrianRightsSuccessAction, (state, action) => {
            state.myProducts = pedestrianOptimisticUpdates.reduceRevokePedestrianMyProducts(state.myProducts, action.payload!);
            pedestrianOptimisticUpdates.applyOptimisticRevokeOnMyFleet(state, action.payload!);
        })
        .addCase(resendInviteActionThunk.pending, (state) => {
            state.loading = true;
        })
        .addCase(resendInviteActionThunk.fulfilled, (state) => {
            state.loading = false;
        })
        .addCase(resendInviteActionThunk.rejected, (state) => {
            state.loading = false;
        })
        .addCase(toggleShowDeletePedestrianDelegeesAction, (state) => {
            state.showDeletePedestrianDelegeesWarning = !state.showDeletePedestrianDelegeesWarning;
        })
        .addCase(deletePedestrianDelegeesSuccessAction, (state, action) => {
            state.myProducts = pedestrianOptimisticUpdates.reduceDeletePedestrianMyProducts(state.myProducts, action.payload.model!)
            pedestrianOptimisticUpdates.applyOptimisticDeleteMyFleet(state, action.payload.model!);
        })
        .addCase(getPedestrianDelegeesMyFleetRequestAction, (state, action) => {
            const pageNumber = action.payload!.pagination.pageNumber;
            state.myFleet.pageNumber = pageNumber;
            state.myFleet.delegees.pages = {
                ...state.myFleet.delegees.pages,
                [pageNumber]: {
                    loading: true,
                    data: [],
                    error: null
                }
            };
            state.myFleet.delegees.pages![pageNumber].data = []
        })
        .addCase(getPedestrianDelegeesMyFleetSuccessAction, (state, action) => {
            state.myFleet.delegees.pages![state.myFleet.pageNumber].loading = false;
            state.myFleet.delegees.pages![state.myFleet.pageNumber].data = action.payload.delegees;
            state.myFleet.delegees.totalPages = action.payload.pages;
            state.myFleet.delegees.totalRecords = action.payload.totalRecords;
        })
        .addCase(getPedestrianDelegeesMyFleetFailureAction, (state, action) => {
            state.error = action.payload.response?.data as ErrorDetailsModel;
            state.myFleet.delegees.pages![state.myFleet.pageNumber].loading = false;
            state.myFleet.delegees.pages![state.myFleet.pageNumber].data = [];
            state.myFleet.delegees.pages![state.myFleet.pageNumber].error = action.payload.response?.data as ErrorDetailsModel;
        })
        .addCase(getPedestrianDelegeeDetailsActionThunk.pending, state => {
            state.delegeeDetails.loading = true;
        })
        .addCase(getPedestrianDelegeeDetailsActionThunk.fulfilled, (state, p) => {
            state.delegeeDetails.loading = false;
            state.delegeeDetails.data = p.payload;
        })
        .addCase(getPedestrianDelegeeDetailsActionThunk.rejected, (state, p) => {
            state.delegeeDetails.loading = false;
        })
        .addCase(updatePedestrianDelegeeActionThunk.pending, (state, action) => {
            state.updateState.loading = true;
        })
        .addCase(updatePedestrianDelegeeActionThunk.fulfilled, (state, action) => {
            state.updateState.loading = false;
            state.updateState.error = undefined;
            pedestrianOptimisticUpdates.applyPedestrianUpdateOnDraftMyProducts(state, action.meta.arg.model, action.payload);
            pedestrianOptimisticUpdates.applyOptimisticUpdateOnMyFleet(state, action.meta.arg.model, action.payload);
        })
        .addCase(updatePedestrianDelegeeActionThunk.rejected, (state, action) => {
            state.updateState.loading = false;
            state.updateState.error = action.payload;
        })
        .addMatcher(isLocatedParkingProductsSuccess, (state, action) => {
            state.locatedParkingProducts = mapToLocatedParkingProducts(action.locatedParkingProductsPayload);
        });
});

export const mapToLocatedParkingProducts = (data: LocatedParkingProduct[]): LocatedParkingProductModel[] => {
    const parkingProducts = data?.flatMap((x) => x.parkingProducts).filter((x) => x.productType === ParkingProductType.PedestrianAccess);

    if (parkingProducts === undefined || parkingProducts.length === 0) {
        return [];
    }

    const locatedParkingProducts = parkingProducts?.map((x: ParkingProductModel) => {
        return {
            id: x.id,
            pmcId: x.pmcId,
            pmcName: x.pmcName,
            location: x.location,
            countryCode: x.countryCode,
            placeId: x.placeId,
            contractStartDate: x.contractStartDate,
            productType: x.productType,
        };
    });

    return locatedParkingProducts;
}

export const updateLocationName = (data: DelegatedPedestrianRight[], products: LocatedParkingProductModel[]): DelegatedPedestrianRight[] => {
    data.forEach((x) => {
        x.product.locationName = products?.find((p) => p.placeId === x.product.placeId)?.location ?? "";
    });

    return data;
}

